/* eslint-disable no-underscore-dangle */

import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { withRouter } from 'react-router';

import { getRecommendationAnalytics } from 'Shared/actions/organizations';
import { recommendationKlassDisplayName } from 'Shared/helpers';

import * as api from 'Shared/api';
import Avatar from 'Shared/components/Avatar/Avatar';
import Meter from './Meter';
import ActivityFeed from './ActivityFeed';
import TimeSeriesChart from './components/TimeSeriesChart/TimeSeriesChart';
import DetailNudgesModal from './components/DetailNudgesModal/DetailNudgesModal';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';

import './Analytics.scss';

const percentage = (numerator, denominator) => {
  if (denominator === 0) {
    return 0;
  }
  return Math.round((numerator / denominator) * 100);
};

const SingleUserAnalytics = props => {
  const dispatch = useDispatch();

  const { app } = useApp();
  const {
    match: {
      params: { userId },
    },
  } = props;

  const refContainer = useRef(null);

  const analytics = useSelector(state => state.recommendationAnalytics[userId]);

  const [detailNudges, setDetailNudges] = useState([]);
  const [detailNudgesDescription, setDetailNudgesDescription] = useState('');
  const [detailNudgesFilter, setDetailNudgesFilter] = useState(false);
  useEffect(() => {
    if (detailNudgesFilter) {
      setDetailNudges([]);
      setDetailNudgesDescription('Loading nudges...');

      api.getFilteredRecommendations(detailNudgesFilter).then(response => {
        setDetailNudges(response.data.recommendations);
        setDetailNudgesDescription(response.data.description);
      });
    }

    window.analytics.track('Viewed Single User NudgeBot Analytics', { app_id: app.id });
  }, [detailNudgesFilter]);

  useEffect(() => {
    dispatch(getRecommendationAnalytics({ userId }));
  }, [dispatch, userId]);

  useEffect(() => {
    if (refContainer.current) {
      refContainer.current.scrollIntoView();
    }
  }, [refContainer]);

  if (!analytics) {
    return <div className="p4 text-align-center">Loading analytics...</div>;
  }

  const percentageClosed = percentage(analytics.totals.closed_nudges, analytics.totals.opened_nudges);

  return (
    <div className="nudgebot-analytics d-flex">
      <div className="main-content">
        <div className="pt-3 pl-3 pr-3 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <Avatar avatar={analytics.avatar} height={48} />
            <h3 className="ml-2 mb-0" ref={refContainer}>
              {analytics.name}
            </h3>
          </div>
          <Link to="/recommendation_analytics">Return to leaderboard</Link>
        </div>
        <div className="d-flex flex-wrap">
          <div className="col-sm-12 col-lg-6 col-xl-6 mb-4">
            <div className="mb-2 mt-4 p-3 border rounded  nudgebot-card">
              <h5 className="mt-0 mb-4">CRM Data Quality Score</h5>
              <Meter totalPercentage={percentageClosed} />
              <p className="small text-muted mt-3">
                {`${percentageClosed}% `}
                {`(${analytics.totals.closed_nudges}/${analytics.totals.opened_nudges}) `}
                of opened nudges have been closed.
              </p>
            </div>
          </div>

          <div className="col-sm-12 col-lg-6 col-xl-6 mb-4">
            <div className="mb-2 mt-4 p-3 border rounded nudgebot-card d-flex align-items-center justify-content-center">
              {/* <h5 className="mt-0 mb-4">Leaderboard</h5> */}
              <div className="ranking">
                <strong>{analytics.name}</strong>
                {' is currently ranked'}
                <div className="rank">{analytics.rank_humanized}</div>
                {`out of ${analytics.team_size}`}.
              </div>
            </div>
          </div>

          <div className="col-sm-12 col-lg-6 col-xl-6 mb-4">
            <div className="mb-2 p-3 border rounded nudgebot-card">
              <TimeSeriesChart
                userId={userId}
                title="CRM Data Quality Over Time"
                chartKey="hygiene_timeseries"
                setDetailNudgesFilter={setDetailNudgesFilter}
              />
            </div>
          </div>

          <div className="col-sm-12 col-lg-6 col-xl-6 mb-4">
            <div className="mb-2 p-3 border rounded  nudgebot-card">
              <TimeSeriesChart
                userId={userId}
                title="Nudges Over Time"
                chartKey="events_timeseries"
                setDetailNudgesFilter={setDetailNudgesFilter}
              />
            </div>
          </div>

          <div className="col-sm-12 col-lg-12 col-xl-12">
            <div className="table-container border rounded px-4 pt-4 pb-0 mb-4">
              <h5 className="mt-0 mb-4">Nudge Breakdown</h5>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Nudge Type</th>
                    <th scope="col" className="text-right">
                      Open
                    </th>
                    <th scope="col" className="text-right">
                      Closed
                    </th>
                    <th scope="col" className="text-right">
                      Dismissed
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.hygiene_score_by_recommendation_setting_name
                    .sort((a, b) => b.opened - a.opened)
                    .map(({ recommendation_setting_name, currently_open_nudges, closed_nudges, dismissed_nudges }) => (
                      <tr key={recommendation_setting_name}>
                        <td>{recommendationKlassDisplayName(recommendation_setting_name)}</td>
                        <td className="text-right">
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() =>
                              setDetailNudgesFilter({
                                user_id: userId,
                                recommendation_setting_name,
                                recommendation_status: 'open',
                              })
                            }
                          >
                            {currently_open_nudges}
                          </button>
                        </td>
                        <td className="text-right">
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() =>
                              setDetailNudgesFilter({
                                user_id: userId,
                                recommendation_setting_name,
                                recommendation_status: 'closed',
                              })
                            }
                          >
                            {closed_nudges}
                          </button>
                        </td>
                        <td className="text-right">
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() =>
                              setDetailNudgesFilter({
                                user_id: userId,
                                recommendation_setting_name,
                                recommendation_status: 'dismissed',
                              })
                            }
                          >
                            {dismissed_nudges}
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="sidebar" style={{ flexShrink: 0 }}>
        <ActivityFeed feed={Object.entries(analytics.activity_feed)} />
      </div>

      {detailNudgesFilter && (
        <DetailNudgesModal
          detailNudgesFilter={detailNudgesFilter}
          detailNudgesDescription={detailNudgesDescription}
          setDetailNudgesFilter={setDetailNudgesFilter}
          detailNudges={detailNudges}
        />
      )}
    </div>
  );
};

SingleUserAnalytics.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      workspaceId: PropTypes.string,
    }),
  }).isRequired,
};

export default withRouter(SingleUserAnalytics);
